/**
 * In-system layout tuning (plan §2.7, R4): `src/data/reach-systems.json` and `src/data/factions.json`, Zod-validated
 * here. Kept out of `src/data/schemas.ts` (M3 data has its own schemas, plan §1.3).
 */
import { z } from 'zod'
import factionsJson from '../../data/factions.json'
import { galaxyAnchorIds, galaxyArchetypes, galaxyPoiKinds, galaxyRegionIds, galaxyStarClasses } from '../../data/galaxyConfig'
import reachSystemsJson from '../../data/reach-systems.json'
import type { FactionId } from '../../render/contracts'
import type { FactionKey, StationService, StationType } from './types'

/** Canonical faction order (factions.json order; `sponsorChosen` event payloads index it). */
export const FACTION_KEYS = ['authority', 'tethys', 'meridian', 'varro_security', 'mutual', 'holdfast', 'raider'] as const satisfies readonly FactionKey[]
export const STATION_TYPES = ['hub', 'refinery', 'scrapyard', 'trade-post', 'outpost', 'habitat', 'patrol-base'] as const satisfies readonly StationType[]
export const STATION_SERVICES = [
  'market',
  'repair',
  'outfitting',
  'contracts',
  'chart-buyer',
  'parts-vendor',
  'lease-office',
] as const satisfies readonly StationService[]
/** The lease service: only the home cluster offers it (M2's lease office; the bridge strips it elsewhere). */
export const LEASE_SERVICE = 'lease-office' as const satisfies StationService
const M2_ROLES = ['hub', 'refinery', 'scrapyard'] as const
/** Render faction looks (render/contracts FactionId). */
const LOOKS = [
  'player_lease',
  'raider',
  'first_rush',
  'neutral',
  'authority',
  'tethys',
  'meridian',
  'varro_security',
  'mutual',
  'holdfast',
  'guardian',
  'player_corp',
] as const satisfies readonly FactionId[]

const positive = z.number().positive()
const nonNegative = z.number().nonnegative()
const vec3 = z.tuple([z.number(), z.number(), z.number()])
const numRange = z.tuple([nonNegative, nonNegative]).refine(([lo, hi]) => lo <= hi, { message: 'range min must not exceed max' })
const intRange = z
  .tuple([z.number().int().nonnegative(), z.number().int().nonnegative()])
  .refine(([lo, hi]) => lo <= hi, { message: 'range min must not exceed max' })
const faction = z.enum(FACTION_KEYS)
const stationType = z.enum(STATION_TYPES)
const region = z.enum(galaxyRegionIds)
const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/)
const assetId = z.string().min(1)

// ---------------------------------------------------------------------------------------------
// factions.json
// ---------------------------------------------------------------------------------------------

export const factionsConfigSchema = z.object({
  comment: z.string().optional(),
  factions: z.record(
    faction,
    z.object({
      name: z.string().min(1),
      short: z.string().min(1),
      look: z.enum(LOOKS),
      mapColor: hexColor,
      patrols: z.boolean(),
    }),
  ),
  regionWeights: z.record(
    region,
    z.partialRecord(faction, nonNegative).refine((w) => Object.values(w).some((v) => (v ?? 0) > 0), { message: 'a region needs a positive weight' }),
  ),
  sponsors: z
    .array(faction)
    .min(1)
    .refine((s) => new Set(s).size === s.length, { message: 'sponsors must be unique' }),
  /**
   * The player corporation's stations (strategic owner `'player'`): their render look, and the M3/M2 faction their
   * synthetic sites carry for M2's station definition (standing modifier, contracts). Optional: without it they are not drawn.
   */
  corporation: z.object({ look: z.enum(LOOKS), siteFaction: faction }).strict().optional(),
})
export type FactionsConfig = z.infer<typeof factionsConfigSchema>

/** Parses and validates factions.json (throws a ZodError naming the bad field). */
export function loadFactionsConfig(raw: unknown = factionsJson): FactionsConfig {
  return factionsConfigSchema.parse(raw)
}

// ---------------------------------------------------------------------------------------------
// reach-systems.json
// ---------------------------------------------------------------------------------------------

const stationTypeSchema = z.object({
  assetId,
  label: z.string().min(1),
  services: z.array(z.enum(STATION_SERVICES)).min(1),
  m2Role: z.enum(M2_ROLES),
  factions: z.array(faction).min(1),
})

const homeStationSchema = z.object({
  type: stationType,
  faction,
  name: z.string().min(1),
  /** Relative to the M1 frame origin (the home cluster centre). */
  offset: vec3,
  specialty: z.string().min(1).nullable().optional(),
  /**
   * Services this home station offers on top of its type's (D-TBD): the Charter Hub's `lease-office`, matching M2's
   * `stations.json` hub, so every `StationSite.services` reader (dashboard, Nav, maps) sees where the lease is paid.
   */
  extraServices: z.array(z.enum(STATION_SERVICES)).optional(),
})

export const reachSystemsConfigSchema = z
  .object({
    comment: z.string().optional(),
    stars: z.object({ radiusM: z.record(z.enum(galaxyStarClasses), positive) }),
    bodies: z.object({ shellFactor: z.number().min(1), shellExtraM: nonNegative, tiltDeg: z.number().min(0).max(90) }),
    stationTypes: z.record(stationType, stationTypeSchema),
    archetypeStations: z.record(
      z.enum(galaxyArchetypes),
      z.object({
        count: intRange,
        types: z.partialRecord(stationType, nonNegative).refine((w) => Object.values(w).some((v) => (v ?? 0) > 0), {
          message: 'types need a positive weight',
        }),
      }),
    ),
    regionStationScale: z.record(region, nonNegative),
    minStations: z.record(region, z.number().int().nonnegative()),
    /** The draw weight of an allowed faction that its region does not list. */
    factionFloor: z.number().gt(0),
    placement: z.object({
      anchorWeights: z.object({ body: nonNegative, field: nonNegative, free: nonNegative }),
      bodyAltitudeFactor: numRange,
      bodyAltitudeMinM: positive,
      fieldEdgeOffsetM: nonNegative,
      freeOrbitM: numRange,
      minSeparationM: positive,
      maxTries: z.number().int().positive(),
    }),
    anchors: z.record(z.enum(galaxyAnchorIds), z.array(stationType)),
    homeCluster: z.object({
      fieldIndex: z.number().int().nonnegative(),
      stations: z.array(homeStationSchema).min(1),
      spawnOffset: vec3,
    }),
    relays: z.object({
      orbitFraction: numRange,
      minSeparationDeg: z.number().min(0).max(180),
      yJitterM: nonNegative,
      apertureM: positive,
      shellClearanceM: nonNegative,
      shellStepDeg: z.number().positive().max(90),
    }),
    lanes: z.object({
      archetypes: z.array(z.enum(galaxyArchetypes)),
      anchors: z.array(z.enum(galaxyAnchorIds)),
      factions: z.array(faction),
      maxLanes: z.number().int().nonnegative(),
      ringsPerLane: intRange.refine(([lo]) => lo >= 2, { message: 'a lane needs at least 2 rings' }),
      endClearanceM: nonNegative,
      minLengthM: positive,
      apertureM: positive,
      shellClearanceM: nonNegative,
      downChance: z.record(region, z.number().min(0).max(1)),
    }),
    danger: z.object({
      region: z.record(region, nonNegative),
      archetype: z.partialRecord(z.enum(galaxyArchetypes), nonNegative),
      anchor: z.partialRecord(z.enum(galaxyAnchorIds), nonNegative),
    }),
    pois: z.object({
      assetByKind: z.record(z.enum(galaxyPoiKinds), assetId.nullable()),
      guardianBaseAsset: assetId,
      clearanceM: nonNegative,
      starClearanceFactor: z.number().min(1),
    }),
  })
  .superRefine((c, ctx) => {
    for (const [type, t] of Object.entries(c.stationTypes)) {
      if (t.factions.length !== new Set(t.factions).size) ctx.addIssue({ code: 'custom', message: `stationTypes.${type}: duplicate faction` })
    }
    for (const [type, t] of Object.entries(c.stationTypes)) {
      if (t.services.includes(LEASE_SERVICE)) ctx.addIssue({ code: 'custom', message: `stationTypes.${type}: ${LEASE_SERVICE} stays with the home cluster (homeCluster.stations[].extraServices)` })
    }
    for (const [i, s] of c.homeCluster.stations.entries()) {
      for (const sv of s.extraServices ?? []) {
        if (c.stationTypes[s.type].services.includes(sv)) ctx.addIssue({ code: 'custom', message: `homeCluster.stations[${i}]: a ${s.type} already offers ${sv}` })
      }
      if (!c.stationTypes[s.type].factions.includes(s.faction)) {
        ctx.addIssue({ code: 'custom', message: `homeCluster.stations[${i}]: ${s.faction} does not run a ${s.type}` })
      }
    }
    const names = c.homeCluster.stations.map((s) => s.name)
    if (new Set(names).size !== names.length) ctx.addIssue({ code: 'custom', message: 'homeCluster: station names must be unique' })
    if (!c.homeCluster.stations.some((s) => s.type === 'hub')) ctx.addIssue({ code: 'custom', message: 'homeCluster: needs a hub (the spawn faces it)' })
    const w = c.placement.anchorWeights
    if (!(w.body + w.field + w.free > 0)) ctx.addIssue({ code: 'custom', message: 'placement.anchorWeights: need a positive weight' })
  })
export type ReachSystemsConfig = z.infer<typeof reachSystemsConfigSchema>

/** Parses and validates reach-systems.json (throws a ZodError naming the bad field). */
export function loadReachSystemsConfig(raw: unknown = reachSystemsJson): ReachSystemsConfig {
  return reachSystemsConfigSchema.parse(raw)
}

let cachedSystems: ReachSystemsConfig | null = null
let cachedFactions: FactionsConfig | null = null

/** The bundled reach-systems.json, parsed once. */
export function defaultReachSystemsConfig(): ReachSystemsConfig {
  cachedSystems ??= loadReachSystemsConfig()
  return cachedSystems
}

/** The bundled factions.json, parsed once. */
export function defaultFactionsConfig(): FactionsConfig {
  cachedFactions ??= loadFactionsConfig()
  return cachedFactions
}
